import { useTheme } from '@chakra-ui/system';
import { rgba } from 'polished';
import { FC, forwardRef } from 'react';
import Select, { Props, StylesConfig as Styles } from 'react-select';

type SelectVariant = 'searchable' | 'not-searchable' | 'modal' | 'custom';

interface CustomSelectProps {
  variant?: SelectVariant;
  maxWidth?: string;
  borderRadius?: string;
  width?: string;
  height?: string;
}

export const baseStyle = (): Styles<{}, false> => {
  const { colors, radii, fontSizes } = useTheme();
  return {
    control: (styles, state) => ({
      ...styles,
      height: 50,
      border: `1px solid ${colors.secondary[300]}`,
      borderRadius: radii.base,
      paddingLeft: 10,
      fontWeight: 'normal',
      fontSize: fontSizes.md,
      color: colors.secondary[500],
      zIndex: 998,
      boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[400]}` : 'none',
      // @ts-ignore
      ':hover': {
        border: `1px solid ${colors.secondary[400]}`,
      },
    }),
    group: styles => ({
      ...styles,
      borderRadius: radii.base,
      paddingTop: 0,
      zIndex: 999,
    }),
    menu: styles => ({
      ...styles,
      zIndex: 999,
      borderRadius: radii.base,
      paddingTop: 0,
      boxShadow: `0px 10px 30px ${rgba(colors.black, 0.1)}`,
    }),
    menuList: styles => ({
      ...styles,
      paddingTop: 0,
      zIndex: 999,
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      fontSize: fontSizes['md'],
      margin: '5px',
      padding: '10px 16px',
      fontWeight: 600,
      backgroundColor: isSelected ? colors.primary[100] : isFocused ? colors.primary[100] : undefined,
      color: colors.secondary[700],
      width: 'calc(100% - 10px)',
      //@ts-ignore
      ':active': {
        //@ts-ignore
        ...styles[':active'],
        backgroundColor: isSelected ? colors.primary[100] : rgba(colors.primary[100], 0.3),
      },
    }),
    placeholder: styles => ({ ...styles, color: colors.secondary[400], fontSize: fontSizes.md, whiteSpace: 'nowrap' }),
    dropdownIndicator: (styles, { selectProps: { menuIsOpen } }) => ({
      ...styles,
      transform: menuIsOpen ? 'rotate(180deg)' : '',
      //@ts-ignore
      '& svg': {
        fill: menuIsOpen ? colors.primary[500] : colors.secondary[400],
      },
    }),
    clearIndicator: styles => ({ ...styles, ':hover': { ...styles[':hover'], color: colors.other.CTA } }),
  };
};

// eslint-disable-next-line react/display-name
const CustomSelect: FC<Props<{}> & CustomSelectProps> = forwardRef(({ variant = 'searchable', ...props }, ref) => {
  const { colors, radii, fontSizes } = useTheme();
  const simpleStyle: Styles<{}, false> = {
    ...baseStyle(),
    control: styles => ({
      ...styles,
      height: 38,
      border: 'none',
      borderRadius: radii.base,
      paddingLeft: 10,
      fontWeight: 500,
      fontSize: fontSizes.md,
      color: colors.secondary[500],
      zIndex: 998,
      maxWidth: props.maxWidth ?? '68%',
      // @ts-ignore
      ':hover': {
        background: colors.primary[100],
      },
    }),
  };

  const modalStyles: Styles<{}, false> = {
    ...baseStyle(),
    menuPortal: styles => ({ ...styles, zIndex: 9999 }),
  };

  const customStyle: Styles<{}, false> = {
    ...baseStyle(),
    control: styles => ({
      ...styles,
      borderRadius: props.borderRadius ?? radii.base,
      height: props.height ?? 50,
      width: props.width ?? '100%',
    }),
  };

  const styles: { [key: string]: Styles<{}, false> } = {
    searchable: baseStyle(),
    'not-searchable': simpleStyle,
    modal: modalStyles,
    custom: customStyle,
  };

  const selectedStyle = styles[variant as string];

  return (
    <Select
      // @ts-ignore
      ref={ref}
      components={{ IndicatorSeparator: null }}
      styles={selectedStyle}
      {...props}
    />
  );
});

export default CustomSelect;
